import React, { useEffect, useState } from 'react';
import api from '../api';

const CURRENT_YEAR = new Date().getFullYear();

function formatHour(v) {
  if (v === null || v === undefined) return 0;
  return Number.isInteger(v) ? v : v.toFixed(1);
}

function AbsenceDetailModal({ studid, name, year, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/admin/absence-status/${studid}/detail`, { params: { year } })
      .then((res) => setRows(res.data))
      .finally(() => setLoading(false));
  }, [studid, year]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{name} 결석내역 ({year}년)</h3>
          <button className="btn-ghost" type="button" onClick={onClose}>닫기</button>
        </div>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>날짜</th>
                <th>과목</th>
                <th>교육내용</th>
                <th>출결상태</th>
                <th>1교시</th>
                <th>2교시</th>
                <th>3교시</th>
                <th>4교시</th>
                <th>인정시수</th>
                <th>사유</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx}>
                  <td>{r.satdate}</td>
                  <td>{r.subject}</td>
                  <td>{r.chapter}</td>
                  <td>{r.attstatus}</td>
                  <td>{r.period1 ?? ''}</td>
                  <td>{r.period2 ?? ''}</td>
                  <td>{r.period3 ?? ''}</td>
                  <td>{r.period4 ?? ''}</td>
                  <td>{r.crdtime}</td>
                  <td>{r.reason}</td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="empty-row">결석 기록이 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ProgressCell({ pct }) {
  return (
    <td className="hr-progress-cell">
      <div className="hr-mini-progress-wrap">
        <div className="hr-mini-progress-track">
          <div className="hr-mini-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="hr-mini-progress-pct">{pct}%</span>
      </div>
    </td>
  );
}

function ProgressModal({ studid, name, grade, classtype, classNo, year, onClose }) {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/admin/student-progress/${studid}`, {
        params: { year, grade, classtype: classtype || 'R', class_no: classNo },
      })
      .then((res) => setChapters(res.data))
      .finally(() => setLoading(false));
  }, [studid, grade, classtype, classNo, year]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{name} 과목별 진도표 ({new Date().toISOString().slice(0, 10)} 기준)</h3>
          <button className="btn-ghost" type="button" onClick={onClose}>닫기</button>
        </div>
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>과목</th>
                <th>교육내용</th>
                <th style={{ whiteSpace: 'nowrap' }}>정상</th>
                <th style={{ whiteSpace: 'nowrap' }}>학생</th>
                <th style={{ whiteSpace: 'nowrap' }}>부족</th>
                <th style={{ whiteSpace: 'nowrap' }}>진도율</th>
              </tr>
            </thead>
            <tbody>
              {chapters.map((c, idx) => {
                const prev = chapters[idx - 1];
                const showSubject = !prev || prev.group_subject !== c.group_subject;
                const dueTotal = c.due_hours || 0;
                const doneTotal = c.done_hours || 0;
                const shortfall = Math.max(0, dueTotal - doneTotal);
                const pct = dueTotal > 0 ? Math.min(100, Math.round((doneTotal / dueTotal) * 100)) : 0;
                return (
                  <tr key={c.amtcode}>
                    <td>{showSubject ? c.group_subject : ''}</td>
                    <td>{c.amt_chapter}</td>
                    <td>{formatHour(dueTotal)}</td>
                    <td>{formatHour(doneTotal)}</td>
                    <td className={shortfall > 0 ? 'hr-below-legal' : ''}>{formatHour(shortfall)}</td>
                    <ProgressCell pct={pct} />
                  </tr>
                );
              })}
              {!loading && chapters.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty-row">진도 자료가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function AbsenceStatus() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [grade, setGrade] = useState('');
  const [classtype, setClasstype] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailTarget, setDetailTarget] = useState(null);
  const [progressTarget, setProgressTarget] = useState(null);

  useEffect(() => {
    setLoading(true);
    api
      .get('/admin/absence-status', {
        params: {
          year,
          grade: grade === '' ? undefined : grade,
          classtype: classtype === '' ? undefined : classtype,
        },
      })
      .then((res) => setRows(res.data))
      .finally(() => setLoading(false));
  }, [year, grade, classtype]);

  return (
    <div className="page">
      <div className="page-header">
        <h2>결시현황</h2>
      </div>

      <div className="card">
        <div className="form-row">
          <div className="form-field form-field-narrow">
            <label>연도</label>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="form-field form-field-narrow">
            <label>학년</label>
            <select value={grade} onChange={(e) => setGrade(e.target.value === '' ? '' : Number(e.target.value))}>
              <option value="">전체</option>
              <option value={1}>1학년</option>
              <option value={2}>2학년</option>
              <option value={3}>3학년</option>
            </select>
          </div>
          <div className="form-field form-field-narrow">
            <label>구분</label>
            <select value={classtype} onChange={(e) => setClasstype(e.target.value)}>
              <option value="">전체</option>
              <option value="R">정규반</option>
              <option value="M">면장반</option>
            </select>
          </div>
        </div>

        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>학년</th>
                <th>구분</th>
                <th>반</th>
                <th>학번</th>
                <th>이름</th>
                <th>결석일수</th>
                <th>결석시수</th>
                <th></th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.studid}>
                  <td>{r.grade}</td>
                  <td>{r.classtype === 'M' ? '면장반' : '정규반'}</td>
                  <td>{r.class_no}</td>
                  <td>{r.studid}</td>
                  <td>{r.name}</td>
                  <td>{r.absence_days}</td>
                  <td>{formatHour(r.absence_hours)}</td>
                  <td>
                    <button
                      className="btn-link"
                      type="button"
                      onClick={() => setDetailTarget({ studid: r.studid, name: r.name })}
                    >
                      결석내역
                    </button>
                  </td>
                  <td>
                    <button
                      className="btn-link"
                      type="button"
                      onClick={() =>
                        setProgressTarget({
                          studid: r.studid,
                          name: r.name,
                          grade: r.grade,
                          classtype: r.classtype,
                          classNo: r.class_no,
                        })
                      }
                    >
                      과목별 진도표
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="empty-row">결석 기록이 있는 학생이 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {detailTarget && (
        <AbsenceDetailModal
          studid={detailTarget.studid}
          name={detailTarget.name}
          year={year}
          onClose={() => setDetailTarget(null)}
        />
      )}
      {progressTarget && (
        <ProgressModal
          studid={progressTarget.studid}
          name={progressTarget.name}
          grade={progressTarget.grade}
          classtype={progressTarget.classtype}
          classNo={progressTarget.classNo}
          year={year}
          onClose={() => setProgressTarget(null)}
        />
      )}
    </div>
  );
}
